const PROBE_ORIGIN = 'https://internal.invalid';
const MAX_LENGTH = 2000;

const isUnsafeForm = (value: string) =>
  !value.startsWith('/') ||
  value.startsWith('//') ||
  value.startsWith('/\\') ||
  // eslint-disable-next-line no-control-regex
  /[\\\u0000-\u001f\u007f]/.test(value);

/**
 * Return a same-origin path for a post-login `next` value, or null.
 *
 * Rejects absolute and protocol-relative URLs, backslashes, control
 * characters and encoded variants of those (checked after repeated
 * decoding), and login routes that would loop.
 */
export const safeInternalPath = (
  raw: string | null | undefined,
): string | null => {
  if (!raw || raw.length > MAX_LENGTH) return null;
  let probe = raw;
  for (let i = 0; i < 3; i += 1) {
    if (isUnsafeForm(probe)) return null;
    let decoded: string;
    try {
      decoded = decodeURIComponent(probe);
    } catch {
      return null;
    }
    if (decoded === probe) break;
    probe = decoded;
  }
  if (isUnsafeForm(probe)) return null;
  let url: URL;
  try {
    url = new URL(raw, PROBE_ORIGIN);
  } catch {
    return null;
  }
  if (url.origin !== PROBE_ORIGIN) return null;
  if (url.pathname === '/login' || url.pathname.startsWith('/login/'))
    return null;
  return `${url.pathname}${url.search}${url.hash}`;
};

export const loginPathFor = (pathname: string, search = '') => {
  const target = safeInternalPath(`${pathname}${search}`);
  if (!target || target === '/') return '/login';
  return `/login?next=${encodeURIComponent(target)}`;
};
